/* eslint-disable no-nested-ternary */
import {
    getChildCustomerDetails,
    getCustomerDetails,
} from 'network/settings-product';
import * as React from 'react';
import { StylesProps } from 'theme/jss-types';
import { Customer, TableColumn } from './settings.types';
import {
    Button,
    Image,
    message,
    Spin,
    Modal,
} from 'antd';
import { subAccountColumns } from './settings.constants';
import { accountStyles } from './settings.styles';
import EditAccount from './edit-account';
import { HocOptions } from 'components/common/generic-hoc.types';
import GenericHoc from 'components/common/generic-hoc';
import { ReduxStore } from 'reducers/redux.types';
import { CustomerPortalConfig, Master } from 'types/master-data-types';
import { useTranslation } from 'react-i18next';
import { getRoles } from 'network/user-management';
import EditUser from '../user-management/EditCustomerUser';
import { User } from '../user-management/user.types';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
import { ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import authHelper from 'auth/auth-helper';
import useIsMobile from 'hooks/useIsMobile';

interface AccountProps extends StylesProps<ReturnType<typeof accountStyles>> {
    master: Master;
    phone_regex: RegExp;
    customer_portal_config: CustomerPortalConfig;
    password_policy: any;
    disableForgotPassword: boolean;
}

const AccountSettings = (props: AccountProps) => {
    const {
        classes,
        customer_portal_config,
        phone_regex,
        password_policy,
        master,
        disableForgotPassword,
    } = props;
    const { t } = useTranslation();
    const [customer, setCustomer] = React.useState<Partial<Customer>>({});
    const [childs, setChilds] = React.useState<any>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [editVisible, setEditVisible] = React.useState<boolean>(false);
    const [editCustomerUserVisible, setEditCustomerUserVisible] = React.useState<boolean>(false);
    const [toggle, setToggle] = React.useState<boolean>(false);
    const customerDetails = master?.CustomerUserDetails;
    const showUserManagement = customerDetails?.is_customer_user;
    const customerUser = customerDetails?.is_customer_user;
    const customerPermissions = master?.customerUserPermissions;
    const [editData, setEditData] = React.useState<User | null>(null);
    const isMobile = useIsMobile();
    const loadDetails = async () => {
        setLoading(true);
        const response = await getCustomerDetails();
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setCustomer(response?.data[0] || {});
        }
        setLoading(false);
    };

    const loadChilds = async () => {
        setLoading(true);
        const response = await getChildCustomerDetails();
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setChilds(response?.data || []);
        }
        setLoading(false);
    };


    const openEditForm = () => {
        setEditVisible(true);
    };

    const fetchUserRolesedit = async (userId: number) => {
        const rolesData = await getRoles(userId);
        if (rolesData.isSuccess) {
            setEditData(rolesData?.data?.userRoleGroupArr);
            setCustomer(rolesData?.data?.userRoleGroupArr[0] || {});
        } else {
            message.error(rolesData.errorMessage);
        }
        setLoading(false);
    };

    React.useEffect(() => {
        if (customerUser) {
            const customerUserId = customerDetails?.id;
            fetchUserRolesedit(customerUserId);
        } else {
            loadDetails();
        }
        loadChilds();
    }, [toggle]);

    const openCustomerUserEditForm = async () => {
        setLoading(true);
        const customerUserId = customerDetails?.id;
        await fetchUserRolesedit(customerUserId);
        setEditCustomerUserVisible(true);
    };

    const renderTitle = () => {
        return (
            <div className={classes.title}>
                <span>{t('account_settings_heading')}</span>
                <div>
                    <Button
                        type="primary"
                        onClick={() => {
                            if (showUserManagement) {
                                openCustomerUserEditForm();
                            } else {
                                openEditForm();
                            }
                        }}
                        className={classes.newButton}
                        disabled={disableForgotPassword}
                        icon={<EditOutlined />}
                    >
                        {t('edit')}
                    </Button>
                </div>
            </div>
        );
    };


    const renderBankTitle = () => {
        if (!customer_portal_config?.show_bank_details
            || (customerPermissions?.setting && customerDetails?.is_customer_user)
        ) {
            return null;
        }
        return (
            <div className={classes.title}>
                {t('bank_details_heading')}
            </div>
        );
    };

    const renderKycTitle = () => {
        if (!customer_portal_config?.show_kyc_details
            || (customerPermissions?.setting && customerDetails?.is_customer_user)
        ) {
            return null;
        }
        return (
            <div className={classes.title}>
                {t('kyc_details_heading')}
            </div>
        );
    };

    const renderBasic = () => {
        return (
            <div className={classes.row}>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_code')}</span>
                    <span className={classes.pairValue}>
                        {!customerUser ? customer.code || '-' : customer.user_code || '-'}
                    </span>
                </div>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('address_name')}</span>
                    <span className={classes.pairValue}>{customer.name || '-'}</span>
                </div>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_username')}</span>
                    <span className={classes.pairValue}>{customer.username || '-'}</span>
                </div>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_parent_name')}</span>
                    <span className={classes.pairValue}>{customer.parent_name || '-'}</span>
                </div>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_created_on')}</span>
                    <span className={classes.pairValue}>N/A</span>
                </div>
            </div>
        );
    };

    const renderImage = (url?: string) => {
        if (!url) {
            return '-';
        }
        return (
            <Image
                height={32}
                src={url}
                alt={url}
            />
        );
    };

    const renderContacts = () => {
        return (
            <div className={classes.row}>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_phone')}</span>
                    <span className={classes.pairValue}>
                        {!customerUser ? customer?.phone_list?.join(', ') || '-' : customer.phone || '-'}
                    </span>
                </div>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_email')}</span>
                    <span className={classes.pairValue}>
                        {!customerUser ? customer?.email_list?.join(', ') || '-' : customer.email || '-'}
                    </span>
                </div>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_status')}</span>
                    <span className={classes.pairValue}>
                        {!customerUser ? (customer.is_active ? 'Active' : 'Inactive') : 'Active'}
                    </span>
                </div>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_signature')}</span>
                    <span className={classes.pairValue}>
                        {renderImage(customer.signature_url)}
                    </span>
                </div>
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('account_seal')}</span>
                    <span className={classes.pairValue}>
                        {renderImage(customer.stamp_url)}
                    </span>
                </div>
            </div>
        );
    };

    const renderGstNumberRow = () => {
        return (
            <div className={classes.lastRow}>
                { customer_portal_config?.show_gst_details ? (
                    <div className={classes.pair}>
                        <span className={classes.pairTitle}>{t('account_gstin')}</span>
                        <span className={classes.pairValue}>{customer.customer_gstin_number || '-'}</span>
                    </div>
                ) : null }
                { customer_portal_config?.show_customer_iec_number ? (
                    <div className={classes.pair}>
                        <span className={classes.pairTitle}>{t('account_iec')}</span>
                        <span className={classes.pairValue}>{customer?.customer_iec_number || '-'}</span>
                    </div>
                ) : null }
                <div className={classes.pair}>
                    <span className={classes.pairTitle}>{t('logo')}</span>
                    <span className={classes.pairValue}>{customer?.logo_url ? renderImage(customer?.logo_url) : '-'}</span>
                </div>
            </div>
        );
    };

    const handleAddUserCancel = () => {
        setEditCustomerUserVisible(false);
        setEditData(null); // Reset edit data
    };

    const handleAddUserSubmit = () => {
        setEditData(null); // Reset edit data
        setEditCustomerUserVisible(false);
    };

    const renderBody = () => {
        if (loading) {
            return (
                <Spin
                    style={{
                        marginTop: '6%',
                        marginBottom: '2%',
                    }}
                />
            );
        }
        if (editVisible) {
            return (
                <EditAccount
                    phone_regex={phone_regex}
                    config={customer_portal_config}
                    customerDetails={customer}
                    password_policy={password_policy}
                    disableForgotPassword={disableForgotPassword}
                    onClose={(reload) => {
                        setEditVisible(false);
                        if (reload) {
                            setToggle(!toggle);
                        }
                    }}
                />
            );
        }
        if (editCustomerUserVisible) {
            return (
                <Modal
                    title={t('Edit User')}
                    visible={editCustomerUserVisible}
                    onCancel={handleAddUserCancel}
                    style={{ left: '460px', top: '-8px' }}
                    footer={null}
                >
                    <EditUser
                        editData={editData}
                        onSubmit={handleAddUserSubmit}
                        onCancel={handleAddUserCancel}
                        password_policy={password_policy}
                    />
                </Modal>
            );
        }
        return (
            <div className={classes.details}>
                {renderBasic()}
                {renderContacts()}
                {renderGstNumberRow()}
            </div>
        );
    };

    const renderBankDetails = () => {
        if (!customer_portal_config?.show_bank_details
            || (customerPermissions?.setting && customerDetails?.is_customer_user)) {
            return null;
        }
        return (
            <div className={classes.row}>
                <div className={classes.bankpair}>
                    <span className={classes.bankTitle}>{t('bank_account_number')}</span>
                    <span className={classes.pairValue}>{customer.bank_details?.account_number || '-'}</span>
                </div>
                <div className={classes.bankpair}>
                    <span className={classes.bankTitle}>{t('bank_code')}</span>
                    <span className={classes.pairValue}>{customer.bank_details?.bank_code || '-'}</span>
                </div>
                <div className={classes.bankpair}>
                    <span className={classes.bankTitle}>{t('branch_code')}</span>
                    <span className={classes.pairValue}>{customer.bank_details?.branch_code || '-'}</span>
                </div>
                <div className={classes.bankpair}>
                    <span className={classes.bankTitle}>{t('bank_name')}</span>
                    <span className={classes.pairValue}>{customer.bank_details?.bank_name || '-'}</span>
                </div>
            </div>
        );
    };

    const renderKycDetails = () => {
        if (!customer_portal_config?.show_kyc_details
            || (customerPermissions?.setting && customerDetails?.is_customer_user)
        ) {
            return null;
        }
        return (
            <div className={classes.row}>
                <div className={classes.bankpair}>
                    <span className={classes.bankTitle}>{t('kyc_doc_type')}</span>
                    <span className={classes.pairValue}>{customer.kyc_details?.kyc_doc_type || '-'}</span>
                </div>
                <div className={classes.bankpair}>
                    <span className={classes.bankTitle}>{t('kyc_doc_no')}</span>
                    <span className={classes.pairValue}>{customer.kyc_details?.kyc_doc_number || '-'}</span>
                </div>
                <div className={classes.bankpair}>
                    <span className={classes.bankTitle}>{t('kyc_doc_front')}</span>
                    <span className={classes.pairValue}>
                        {renderImage(customer.kyc_details?.kyc_url)}
                    </span>
                </div>
                <div className={classes.bankpair}>
                    <span className={classes.bankTitle}>{t('kyc_doc_back')}</span>
                    <span className={classes.pairValue}>
                        {renderImage(customer.kyc_details?.kyc_back_url)}
                    </span>
                </div>
            </div>
        );
    };

    const renderSubTitle = () => {
        return (
            <div className={classes.subTitle}>
                <span>{t('sub_accounts_heading')}</span>
                <Button
                    disabled
                    type="primary"
                    className={classes.newSubButton}
                >
                    {t('add_sub_account')}
                </Button>
            </div>
        );
    };

    const renderText = (text: string | number) => {
        return (
            <div
                className={classes.cellValue}
            >
                {text}
            </div>
        );
    };

    const renderColumn = (
        text: string,
        row: Customer,
        column: string,
        index: number,
    ) => {
        switch (column) {
            case 'index': return renderText(index + 1);
            case 'phone_list': return renderText(row.phone_list[0]);
            case 'is_active': return renderText(row.is_active ? 'Active' : 'Inactive');
            default: return renderText(text as string);
        }
    };

    const renderColumns = () => {
        const columns: any = subAccountColumns.map((column: TableColumn) => {
            return {
                key: column.key,
                title: t(column.pretty_name),
                dataIndex: column.key,
                ellipsis: true,
                bordered: false,
                render: (text: string, row: any, idx: number) => renderColumn(text, row, column.key, idx),
            };
        });
        return columns;
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                columns={renderColumns()}
                loading={loading}
                dataSource={childs}
                pagination={false}
            />
        );
    };

    const handleLogout = async () => {
        setLoading(true);
        await authHelper.logoutUser();
        setLoading(false);
    };

    const renderLogout = () => {
        return (
            <Button
                style={{
                    margin: '8px 40px',
                    color: 'red',
                    border: '1px solid red',
                    borderRadius: '15px',
                }}
                onClick={handleLogout}
            >
                {t('logout')}
                <ArrowRightOutlined />
            </Button>
        );
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ width: '100%', padding: '5px 6px 6px 3px' }}>
                {renderTitle()}
                {renderBody()}
                {renderBankTitle()}
                {renderBankDetails()}
                {renderKycTitle()}
                {renderKycDetails()}
            </div>
            <div style={{ width: '100%', padding: '5px 6px 6px 3px', flexGrow: 1 }}>
                {renderSubTitle()}
                {renderTable()}
            </div>
            {isMobile && renderLogout()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const {
        customer_portal_config,
        phone_regex,
        password_policy,
    } = state?.master?.config || {};
    const { master } = state;
    return {
        master,
        customer_portal_config,
        phone_regex,
        password_policy,
        disableForgotPassword: state?.master?.parts_to_show?.disable_edit_password_from_customer_portal,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: accountStyles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(AccountSettings);
